import { Checkbox, Flex, Input, Text } from "@chakra-ui/react";
import moment from "moment";
import { useContext, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import { ApiKeyCreateResponse } from "@nens/rana/api-v1";
import { RanaModal } from "@nens/ranalib";
import { TenantContext } from "@nens/ranalib";

import {
  PUBLICATION_SEARCH_KEY,
  PUBLICATION_SHARE_KEY_KEY,
  PUBLICATION_SHOW_MAP_KEY,
} from "@/config/query-parameter-consts";

import { CopyButton } from "../../_internal";
import { getId } from "../../store/selectors";
import { usePublicationStore } from "../../store/store";

interface PublicationDetailShareCreateConfirmProps {
  shareKey: ApiKeyCreateResponse;
  onClose: () => void;
}

export function PublicationDetailShareCreateConfirm({
  shareKey,
  onClose,
}: PublicationDetailShareCreateConfirmProps) {
  const [linkToMap, setLinkToMap] = useState(false);
  const publicationId = usePublicationStore(getId);
  const { tenant } = useContext(TenantContext);
  const { value, name, expiresAt } = shareKey;
  // FUTURE: Only support one of the map mode query params
  const shareLink = `${
    window.location.origin
  }/${tenant}/?${PUBLICATION_SHARE_KEY_KEY}=${value}&${PUBLICATION_SEARCH_KEY}=${publicationId}${
    linkToMap ? `&${PUBLICATION_SHOW_MAP_KEY}=true` : ""
  }`;

  const intl = useIntl();

  const titleLinkURL = intl.formatMessage({
    id: "share.link.url_title",
    defaultMessage: "Publication share link",
  });

  return (
    <RanaModal
      isOpen
      onClose={onClose}
      topBar={<FormattedMessage id="share.link.copy_share_link" defaultMessage="Copy share link" />}
      maxW="xl"
      bottomChild={
        <Text fontWeight={300}>
          {expiresAt ? (
            <FormattedMessage
              id="share.link.expires_in"
              defaultMessage="Expires in {time}"
              values={{ time: <Text as="strong">{moment(expiresAt).fromNow(true)}</Text> }}
            />
          ) : (
            <FormattedMessage id="share.link.expires_never" defaultMessage="Never expires" />
          )}
        </Text>
      }
      buttons={[
        {
          children: <FormattedMessage id="button.close" defaultMessage="Close" />,
          variant: "tertiary",
          onClick: onClose,
        },
      ]}
    >
      <Text>
        <FormattedMessage
          id="share.link.successfully_created"
          defaultMessage="Successfully created share key: {name}"
          values={{ name: <Text as="strong">{name}</Text> }}
        />
      </Text>
      <Text my={2}>
        <FormattedMessage
          id="share.link.copy_below"
          defaultMessage="Copy the link below to use the share link. IMPORTANT: This link can only be copied now."
        />
      </Text>
      <Flex>
        <Input
          onFocus={(e) => e.target.select()}
          isReadOnly
          value={shareLink}
          aria-label={titleLinkURL}
          title={titleLinkURL}
        />
        <CopyButton
          copyText={shareLink}
          toastMessage={intl.formatMessage({
            id: "success.copied_share_link",
            defaultMessage: "Copied share link to clipboard.",
          })}
          title={intl.formatMessage({
            id: "share.link.copy_url_title",
            defaultMessage: "Copy link to clipboard",
          })}
        />
      </Flex>
      <Checkbox h="small_button" isChecked={linkToMap} onChange={() => setLinkToMap(!linkToMap)}>
        <FormattedMessage
          id="share.link.open_in_viewer_mode"
          defaultMessage="Link should open in viewer mode"
        />
      </Checkbox>
    </RanaModal>
  );
}
